import React, { useMemo } from 'react';
import { TableCycleData } from '../CycleTable/types';
import { actions as postWeldActions } from '../PostWeldSettings/constants';
import { actions as preHeatActions } from '../PreHeatSettings/constants';
import CycleSettings from '../../enums/cycle-setting.enum';
import { createPortal } from 'react-dom';
import CycleMeasurement from '../../enums/cycle-measurement';
import CycleMeasurementSymbol from '../../enums/cycle-measurement-symbol';
import { convertAbsoluteTemperature, convertTemperatureDelta } from '../../utils/temperature-unit-conversion';

interface ChannelSettingsProps {
  device: TableCycleData;
  position: { top: number; left: number };
  isShow: boolean;
  measurement: CycleMeasurement;
}

const getPostWeldLabelByActionField = (actionField: string) => {
  const action = postWeldActions.find((a) => a.actionField === actionField);
  return action?.labelText || actionField;
};

const getPreHeatLabelByActionField = (actionField: string) => {
  const action = preHeatActions.find((a) => a.actionField === actionField);
  return action?.labelText || actionField;
};

const getRateUnit = (measurement: CycleMeasurement) => `${CycleMeasurementSymbol[measurement]}/h`;

const getTemperatureUnit = (measurement: CycleMeasurement) => CycleMeasurementSymbol[measurement];

const getControllerMeasurement = (device: TableCycleData): CycleMeasurement =>
  device.channelsData?.iotData?.units ?? CycleMeasurement.Celsius;

const PreHeatInfo = ({ device, measurement }: { device: TableCycleData; measurement: CycleMeasurement }) => {
  const recipe = useMemo(() => {
    return device.channelsData.settingsByMode[CycleSettings.PreHeat].settings;
  }, [device]);

  const controllerMeasurement = getControllerMeasurement(device);
  const heatingRate = convertTemperatureDelta(recipe.heatingRate, controllerMeasurement, measurement) as number;
  const finalTemperature = convertAbsoluteTemperature(recipe.finalTemperature, controllerMeasurement, measurement) as number;
  const soakUpperTolerance = convertTemperatureDelta(recipe.soakUpperTolerance, controllerMeasurement, measurement) as number;
  const soakLowerTolerance = convertTemperatureDelta(recipe.soakLowerTolerance, controllerMeasurement, measurement) as number;

  return (
    <div className=" md:translate-y-[-115px]">
      <div className="flex flex-col gap-1 rounded border bg-white px-1 py-1 md:text-xs/3">
        <div>Mode: {device.settingMode}</div>
        <div>
          {getPreHeatLabelByActionField('rate')} ({getRateUnit(measurement)}): {heatingRate}
        </div>
        <div>
          {getPreHeatLabelByActionField('ht')} ({getTemperatureUnit(measurement)}): {finalTemperature}
        </div>
        <div>
          {getPreHeatLabelByActionField('up')} ({getTemperatureUnit(measurement)}): {soakUpperTolerance}
        </div>
        <div>
          {getPreHeatLabelByActionField('lw')} ({getTemperatureUnit(measurement)}): {soakLowerTolerance}
        </div>
      </div>
    </div>
  );
};

const PostWeldInfo = ({ device, measurement }: { device: TableCycleData; measurement: CycleMeasurement }) => {
  const recipe = useMemo(() => {
    return device.channelsData.settingsByMode[CycleSettings.PostWeld].settings.recipe;
  }, [device]);

  const steps = useMemo(() => {
    const recipe = device.channelsData.settingsByMode[CycleSettings.PostWeld].settings.recipe;
    return device.channelsData.settingsByMode['post-weld'].settings.recipeSteps
      .sort((a, b) => {
        return a.order - b.order;
      })
      .filter((s) => s.order < recipe.numberOfSteps);
  }, [device]);

  const sourceMeasurement = getControllerMeasurement(device);
  const upperRampTolerance = convertTemperatureDelta(recipe.upperRampTolerance, sourceMeasurement, measurement) as number;
  const lowerRampTolerance = convertTemperatureDelta(recipe.lowerRampTolerance, sourceMeasurement, measurement) as number;
  const upperHoldTolerance = convertTemperatureDelta(recipe.upperHoldTolerance, sourceMeasurement, measurement) as number;
  const lowerHoldTolerance = convertTemperatureDelta(recipe.lowerHoldTolerance, sourceMeasurement, measurement) as number;

  return (
    <div className="md:translate-y-[-140px]">
      <div className="flex flex-col gap-1 rounded border bg-white px-1 py-1 md:text-xs/3">
        <div>Mode: {device.settingMode}</div>
        <div>
          {getPostWeldLabelByActionField('recipeName')}: {recipe.recipeName}
        </div>
        <div>
          {getPostWeldLabelByActionField('upperRampTolerance')} ({getRateUnit(measurement)}): {upperRampTolerance}
        </div>
        <div>
          {getPostWeldLabelByActionField('lowerRampTolerance')} ({getRateUnit(measurement)}): {lowerRampTolerance}
        </div>
        <div>
          {getPostWeldLabelByActionField('upperHoldTolerance')} ({getTemperatureUnit(measurement)}):{' '}
          {upperHoldTolerance}
        </div>
        <div>
          {getPostWeldLabelByActionField('lowerHoldTolerance')} ({getTemperatureUnit(measurement)}):{' '}
          {lowerHoldTolerance}
        </div>
        <div>Steps: {steps.map((step) => step.mode).join(', ')}</div>
      </div>
    </div>
  );
};

const ChannelSettings: React.FC<ChannelSettingsProps> = ({ device, position, isShow, measurement }) => {
  const showPostWeld = useMemo(() => {
    const isExists = !!device?.channelsData?.settingsByMode[CycleSettings.PostWeld].settings;

    return !!isExists;
  }, [device]);

  const showPreHeat = useMemo(() => {
    const isExists = !!device?.channelsData?.settingsByMode[CycleSettings.PreHeat].settings;

    return !!isExists;
  }, [device]);

  if (!isShow) {
    return null;
  }

  return createPortal(
    <div className="absolute z-50 z-[9999] h-[0]" style={{ ...position }}>
      {showPostWeld && <PostWeldInfo device={device} measurement={measurement} />}
      {showPreHeat && <PreHeatInfo device={device} measurement={measurement} />}
    </div>,
    document.body,
  );
};

export default ChannelSettings;
